/* ==========================================================================
   LUX — Header
   Scroll state, mobile drawer, search panel, announcement rotator.
   No dependencies. One passive scroll listener, rAF-throttled.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var body = document.body;

  /* ---------------------------------------------------------------------
     1. Scroll state: transparent  <->  sticky/solid
     --------------------------------------------------------------------- */

  var SCROLL_THRESHOLD = 100;

  var headerEl = null;
  var ticking = false;
  var isScrolled = false;

  function readThreshold() {
    if (!headerEl) return SCROLL_THRESHOLD;
    var attr = parseInt(headerEl.getAttribute('data-scroll-threshold'), 10);
    return isNaN(attr) ? SCROLL_THRESHOLD : attr;
  }

  function applyScrollState() {
    ticking = false;

    var threshold = readThreshold();
    var y = window.pageYOffset || root.scrollTop || 0;

    // Hysteresis: re-entering the transparent state needs a slightly smaller
    // offset than leaving it, so a scroll that hovers on the boundary cannot
    // flicker between the two backgrounds.
    var shouldBeScrolled = isScrolled
      ? y > threshold - 24
      : y > threshold;

    if (shouldBeScrolled === isScrolled) return;

    isScrolled = shouldBeScrolled;
    body.classList.toggle('lux-scrolled', isScrolled);

    if (headerEl) {
      headerEl.setAttribute('data-scrolled', isScrolled ? 'true' : 'false');
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(applyScrollState);
  }

  /* ---------------------------------------------------------------------
     2. Measurements -> CSS variables (no layout shift, no magic numbers)
     --------------------------------------------------------------------- */

  function measure() {
    var announcement = document.querySelector('[data-lux-announcement]');
    var header = document.querySelector('[data-lux-header]');

    if (announcement) {
      // Measure the un-collapsed height, even while collapsed.
      var h = announcement.getAttribute('data-height');
      root.style.setProperty('--lux-announcement-height', (h ? h : announcement.offsetHeight) + 'px');
    } else {
      root.style.setProperty('--lux-announcement-height', '0px');
    }

    if (header) {
      var inner = header.querySelector('.lux-header__inner');
      if (inner && inner.offsetHeight) {
        root.style.setProperty('--lux-header-height', inner.offsetHeight + 'px');
      }
    }
  }

  /* ---------------------------------------------------------------------
     3. Scroll lock that survives iOS
     --------------------------------------------------------------------- */

  var savedScrollY = 0;
  var lockCount = 0;

  function lockScroll() {
    if (lockCount++ > 0) return;
    savedScrollY = window.pageYOffset || 0;
    body.style.position = 'fixed';
    body.style.top = -savedScrollY + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.classList.add('lux-scroll-locked');
  }

  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.classList.remove('lux-scroll-locked');
    window.scrollTo(0, savedScrollY);
  }

  /* ---------------------------------------------------------------------
     4. Focus trap (shared by drawer, search, cart, quick view)
     --------------------------------------------------------------------- */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    var previouslyFocused = document.activeElement;

    function keydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (typeof onEscape === 'function') onEscape();
        return;
      }
      if (e.key !== 'Tab') return;

      var nodes = Array.prototype.filter.call(
        container.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null || el === document.activeElement; }
      );
      if (!nodes.length) return;

      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', keydown);

    // These panels animate in from visibility:hidden. Calling focus() while the
    // element is still hidden silently does nothing, so we retry until it takes
    // — once on the next frame, again when the transition ends, and a final
    // timeout in case the transition never fires (reduced motion, display:none).
    var target = container.querySelector('[data-lux-autofocus]') ||
                 container.querySelector(FOCUSABLE);

    var focusTimer = null;

    function tryFocus() {
      if (!target) return true;
      if (container.contains(document.activeElement)) return true;

      target.focus();

      if (container.contains(document.activeElement)) {
        container.removeEventListener('transitionend', tryFocus);
        window.clearTimeout(focusTimer);
        return true;
      }
      return false;
    }

    if (target) {
      window.requestAnimationFrame(tryFocus);
      container.addEventListener('transitionend', tryFocus);
      focusTimer = window.setTimeout(tryFocus, 600);
    }

    return function release() {
      document.removeEventListener('keydown', keydown);
      container.removeEventListener('transitionend', tryFocus);
      window.clearTimeout(focusTimer);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }

  // Expose for the cart drawer / quick view modules.
  window.LuxUI = window.LuxUI || {};
  window.LuxUI.trapFocus = trapFocus;
  window.LuxUI.lockScroll = lockScroll;
  window.LuxUI.unlockScroll = unlockScroll;

  /* ---------------------------------------------------------------------
     5. Panels: mobile drawer + search
     --------------------------------------------------------------------- */

  var openPanel = null;
  var releaseTrap = null;

  function closePanel() {
    if (!openPanel) return;

    body.classList.remove('lux-menu-open', 'lux-search-open');
    openPanel.el.setAttribute('aria-hidden', 'true');

    if (openPanel.trigger) openPanel.trigger.setAttribute('aria-expanded', 'false');
    if (releaseTrap) { releaseTrap(); releaseTrap = null; }

    unlockScroll();
    openPanel = null;
  }

  function showPanel(el, bodyClass, trigger) {
    if (openPanel) {
      // Swapping panels: tear the old one down without restoring focus twice.
      body.classList.remove('lux-menu-open', 'lux-search-open');
      openPanel.el.setAttribute('aria-hidden', 'true');
      if (openPanel.trigger) openPanel.trigger.setAttribute('aria-expanded', 'false');
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      unlockScroll();
      openPanel = null;
    }

    body.classList.add(bodyClass);
    el.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    lockScroll();
    releaseTrap = trapFocus(el, closePanel);
    openPanel = { el: el, trigger: trigger };
  }

  window.LuxUI.closePanel = closePanel;

  /* ---------------------------------------------------------------------
     6. Announcement rotator
     --------------------------------------------------------------------- */

  function initAnnouncement(bar) {
    var items = bar.querySelectorAll('.lux-announcement__item');
    if (items.length < 2) return;

    var index = 0;
    var interval = parseInt(bar.getAttribute('data-interval'), 10) || 5000;
    var timer = null;

    function show(next) {
      items[index].classList.remove('is-active');
      items[index].setAttribute('aria-hidden', 'true');
      index = (next + items.length) % items.length;
      items[index].classList.add('is-active');
      items[index].setAttribute('aria-hidden', 'false');
    }

    function start() {
      stop();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      timer = window.setInterval(function () { show(index + 1); }, interval);
    }

    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    bar.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-announcement-nav]');
      if (!btn) return;
      show(index + (btn.getAttribute('data-announcement-nav') === 'next' ? 1 : -1));
      start();
    });

    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else { start(); }
    });

    start();
  }

  /* ---------------------------------------------------------------------
     7. Wire up
     --------------------------------------------------------------------- */

  var listenersBound = false;

  function init() {
    headerEl = document.querySelector('[data-lux-header]');

    // Overlay mode only when a hero is actually on the page.
    if (document.querySelector('.lux-hero')) {
      body.classList.add('lux-has-hero');
    } else {
      body.classList.remove('lux-has-hero');
    }

    measure();

    // Reset state so a Theme Editor re-render re-evaluates from scratch.
    isScrolled = false;
    body.classList.remove('lux-scrolled');
    applyScrollState();

    var announcement = document.querySelector('[data-lux-announcement]');
    if (announcement && announcement.hasAttribute('data-rotate')) {
      initAnnouncement(announcement);
    }

    // Delegated once for the life of the page: the Theme Editor swaps section
    // markup in and out, so elements are looked up per click, never cached.
    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-lux-toggle]');

      if (toggle) {
        var which = toggle.getAttribute('data-lux-toggle');

        if (which === 'menu') {
          var mobileNav = document.querySelector('[data-lux-mobile-nav]');
          if (!mobileNav) return;
          e.preventDefault();
          if (body.classList.contains('lux-menu-open')) closePanel();
          else showPanel(mobileNav, 'lux-menu-open', toggle);
          return;
        }

        if (which === 'search') {
          var search = document.querySelector('[data-lux-search]');
          if (!search) return;
          e.preventDefault();
          if (body.classList.contains('lux-search-open')) closePanel();
          else showPanel(search, 'lux-search-open', toggle);
          return;
        }

        if (which === 'close') {
          e.preventDefault();
          closePanel();
          return;
        }
      }

      // Click-away on the shared overlay.
      if (e.target.closest('[data-lux-overlay]')) {
        closePanel();
        return;
      }

      // Mobile nav accordions.
      var accordion = e.target.closest('[data-lux-accordion]');
      if (accordion) {
        e.preventDefault();
        var item = accordion.closest('.lux-mobile-nav__item');
        var isOpen = item.classList.toggle('is-open');
        accordion.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('orientationchange', measure, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------------------------------------------------------------------
     8. Shopify Theme Editor — re-init after a section re-renders
     --------------------------------------------------------------------- */

  document.addEventListener('shopify:section:load', function () {
    closePanel();
    init();
  });

  document.addEventListener('shopify:section:unload', function () {
    closePanel();
    init();
  });

  // Keep the header transparent while the merchant is editing the hero.
  document.addEventListener('shopify:section:select', measure);
})();
